import React, { useState, useEffect } from 'react';
import { createBanner, updateBanner, uploadFile } from '../../services/adminApi';
import './BannerForm.css';

const BannerForm = ({ banner, onClose }) => {
  const [formData, setFormData] = useState({
    NAME: '',
    PREVIEW_TEXT: '',
    PREVIEW_PICTURE: null,
    ZAG_COLOR: '',
    LINK_OBJ: '',
    PRICE: '',
    SPIS_USL: [],
    ACTIVE: 'Y',
    SORT: 500
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (banner) {
      setFormData({
        NAME: banner.NAME || '',
        PREVIEW_TEXT: banner.PREVIEW_TEXT || '',
        PREVIEW_PICTURE: banner.PREVIEW_PICTURE || null,
        ZAG_COLOR: banner.PROPERTIES?.ZAG_COLOR?.VALUE || banner.PROPERTIES?.ZAG_COLOR?.['~VALUE']?.TEXT || '',
        LINK_OBJ: banner.PROPERTIES?.LINK_OBJ?.VALUE || '',
        PRICE: banner.PROPERTIES?.PRICE?.VALUE || '',
        SPIS_USL: Array.isArray(banner.PROPERTIES?.SPIS_USL?.VALUE) 
          ? banner.PROPERTIES.SPIS_USL.VALUE 
          : banner.PROPERTIES?.SPIS_USL?.VALUE ? [banner.PROPERTIES.SPIS_USL.VALUE] : [],
        ACTIVE: banner.ACTIVE || 'Y',
        SORT: banner.SORT || 500
      });
    }
  }, [banner]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadFile(file);
      setFormData({ ...formData, PREVIEW_PICTURE: result.fileId });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const bannerData = {
        NAME: formData.NAME,
        PREVIEW_TEXT: formData.PREVIEW_TEXT,
        PREVIEW_PICTURE: formData.PREVIEW_PICTURE,
        PROPERTIES: {
          ZAG_COLOR: formData.ZAG_COLOR,
          LINK_OBJ: formData.LINK_OBJ,
          PRICE: formData.PRICE,
          SPIS_USL: formData.SPIS_USL.join('|')
        },
        ACTIVE: formData.ACTIVE,
        SORT: parseInt(formData.SORT)
      };

      if (banner) {
        await updateBanner(banner.ID, bannerData);
      } else {
        await createBanner(bannerData);
      }
      
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const addSpisUsl = () => {
    setFormData({
      ...formData,
      SPIS_USL: [...formData.SPIS_USL, '']
    });
  };

  const updateSpisUsl = (index, value) => {
    const newSpisUsl = [...formData.SPIS_USL];
    newSpisUsl[index] = value;
    setFormData({ ...formData, SPIS_USL: newSpisUsl });
  };

  const removeSpisUsl = (index) => {
    const newSpisUsl = formData.SPIS_USL.filter((_, i) => i !== index);
    setFormData({ ...formData, SPIS_USL: newSpisUsl });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{banner ? 'Edit Banner' : 'Create Banner'}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="banner-form">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.NAME}
              onChange={(e) => setFormData({ ...formData, NAME: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Preview Text</label>
            <textarea
              value={formData.PREVIEW_TEXT}
              onChange={(e) => setFormData({ ...formData, PREVIEW_TEXT: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {uploading && <span className="upload-status">Uploading...</span>}
            {formData.PREVIEW_PICTURE && !uploading && (
              <span className="upload-status">✓ Image uploaded</span>
            )}
          </div>

          <div className="form-group">
            <label>Title (ZAG_COLOR)</label>
            <input
              type="text"
              value={formData.ZAG_COLOR}
              onChange={(e) => setFormData({ ...formData, ZAG_COLOR: e.target.value })}
              placeholder="e.g., Топ туры <span>от BUYME</span>"
            />
          </div>

          <div className="form-group">
            <label>Link (LINK_OBJ)</label>
            <input
              type="url"
              value={formData.LINK_OBJ}
              onChange={(e) => setFormData({ ...formData, LINK_OBJ: e.target.value })}
              placeholder="https://example.com"
            />
          </div>

          <div className="form-group">
            <label>Price (PRICE)</label>
            <input
              type="text"
              value={formData.PRICE}
              onChange={(e) => setFormData({ ...formData, PRICE: e.target.value })}
              placeholder="от $3 500"
            />
          </div>

          <div className="form-group">
            <label>Services List (SPIS_USL)</label>
            {formData.SPIS_USL.map((item, index) => (
              <div key={index} className="spis-usl-item">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => updateSpisUsl(index, e.target.value)}
                  placeholder="Service item"
                />
                <button type="button" onClick={() => removeSpisUsl(index)}>Remove</button>
              </div>
            ))}
            <button type="button" onClick={addSpisUsl} className="add-item-btn">
              + Add Service
            </button>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Active</label>
              <select
                value={formData.ACTIVE}
                onChange={(e) => setFormData({ ...formData, ACTIVE: e.target.value })}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                value={formData.SORT}
                onChange={(e) => setFormData({ ...formData, SORT: e.target.value })}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="btn-save">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BannerForm;

